import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { RolesService } from '../roles.service'
import { RolesRepository } from '../roles.repository'
import { RoleSearchParams } from '../models/role-search.model'
import { Role } from '../models/role.entity'
import { Page } from '../../../models/util.model'

interface RolesListingState {
  filters?: RoleSearchParams
  data?: Role[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const ROLES_LISTING_INITIAL_STATE: RolesListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class RolesListingStore {
  constructor(
    private rolesService: RolesService,
    private rolesRepository: RolesRepository,
  ) {}

  private state = signal<RolesListingState>(ROLES_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly roles = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(params?: RoleSearchParams, returnAll?: boolean): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.rolesRepository.searchRoles$(params ?? {}, returnAll).subscribe({
        next: (data) => {
          resolve()
          this.setData(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async delete(role: Role): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.rolesRepository.alert('ROLE_DELETION', role).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.rolesService.delete$(role._id).subscribe({
            next: () => {
              resolve(true)
              this.rolesRepository.notify({
                code: 'ROLE_DELETED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  // Reducers

  setData(data: Page<Role>) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: RoleSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: RoleSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: RoleSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof RoleSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
